import { Flex } from "@chakra-ui/react";
import { useState } from "react";
import { useIntl } from "react-intl";

import { PublicationRead } from "@nens/rana/api-v1";
import { AddIcon, RanaButton, RanaModal } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";

import { EditCancelSubmitButtons } from "@/components/edit-cancel-submit-buttons";
import { MarkdownExplanation } from "@/components/markdown-explanation";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";

import { usePublicationCreate } from "./hooks/use-create";

export type PublicationFormValues = {
  name: string;
  description: string;
};

type CreatePublicationProps = {
  setPublicationId: (id: string) => void;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export function CreatePublication({
  setPublicationId,
  isOpen,
  onOpen,
  onClose,
}: CreatePublicationProps) {
  const intl = useIntl();

  const onSuccess = (data: PublicationRead) => {
    onClose();
    setPublicationId(data.id);
  };

  const { validate, submit, isMutating } = usePublicationCreate({ onSuccess });

  const buttonText = intl.formatMessage({
    id: "publicaton.create_publication",
    defaultMessage: "Create publication",
  });

  const [isPreviewing, setIsPreviewing] = useState(false);

  const { canWriteProject } = useProjectActionAllowed();
  if (!canWriteProject) return null;

  return (
    <>
      <RanaButton ml="auto" leftIcon={<AddIcon />} variant="primary" onClick={onOpen}>
        {buttonText}
      </RanaButton>

      <RanaModal isOpen={isOpen} onClose={onClose} topBar={buttonText}>
        <RanaForm
          initialValues={{
            name: "",
            description: "",
          }}
          validate={validate}
          doSubmit={submit}
        >
          <RanaForm.Input
            field="name"
            isRequired
            label={intl.formatMessage({
              id: "newpublication.name.label",
              defaultMessage: "Title",
            })}
          />

          <RanaForm.Textarea
            field="description"
            label={intl.formatMessage({
              id: "newpublication.description.label",
              defaultMessage: "Description",
            })}
            h="3xs"
            isPreviewingMarkdown={isPreviewing}
          />
          <RanaForm.Errors />

          <Flex
            gap={4}
            alignItems="center"
            direction="row"
            w="full"
            my={4}
            justifyContent="flex-end"
          >
            <MarkdownExplanation />
            {canWriteProject && (
              <EditCancelSubmitButtons
                isMutating={isMutating}
                isEditing
                isPreviewing={isPreviewing}
                previewButtonProps={{ onClick: () => setIsPreviewing(true), variant: "secondary" }}
                submitButtonProps={{
                  as: RanaForm.SubmitButton,
                  isLoading: isMutating,
                  variant: "primary",
                }}
                cancelButtonProps={{ onClick: onClose, variant: "tertiary" }}
                editButtonProps={{ onClick: () => setIsPreviewing(false), variant: "secondary" }}
              />
            )}
          </Flex>
        </RanaForm>
      </RanaModal>
    </>
  );
}
